import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  updateDoc,
  doc,
  deleteDoc,
  orderBy,
  limit,
  Timestamp,
} from "firebase/firestore";
import { db } from "../config/firebase";

const generateUniqueId = () => {
  return Math.random().toString(36).substring(2, 8);
};

// Crear enlace seguro para invoice
export const createSecureInvoiceLink = async (linkData) => {
  try {
    const linkId = generateUniqueId();
    const now = new Date();
    const expiresAt = new Date(now.getTime() + 24 * 60 * 60 * 1000); // 24 horas

    const linkDoc = {
      linkId,
      invoiceData: linkData.invoiceData,
      email: linkData.email || "",
      createdAt: Timestamp.fromDate(now),
      expiresAt: Timestamp.fromDate(expiresAt),
      isUsed: false,
      isExpired: false,
      createdBy: linkData.createdBy || "admin",
    };

    await addDoc(collection(db, "secureInvoiceLinks"), linkDoc);

    return {
      success: true,
      linkId,
      fullUrl: `${window.location.origin}/invoice-summary?linkId=${linkId}`,
      expiresAt: expiresAt.toISOString(),
    };
  } catch (error) {
    console.error("Error creating secure invoice link:", error);
    throw new Error("Failed to create secure invoice link");
  }
};

// Validar enlace seguro para invoice
export const validateSecureInvoiceLink = async (linkId) => {
  try {
    const q = query(
      collection(db, "secureInvoiceLinks"),
      where("linkId", "==", linkId),
      limit(1)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return {
        isValid: false,
        message: "Link not found",
        status: "not_found",
      };
    }

    const linkDoc = querySnapshot.docs[0];
    const linkData = linkDoc.data();

    if (linkData.isUsed) {
      return {
        isValid: false,
        message: "This link has already been used",
        status: "already_used",
      };
    }

    const now = new Date();
    const expiresAt = linkData.expiresAt.toDate();

    if (now > expiresAt) {
      await updateDoc(doc(db, "secureInvoiceLinks", linkDoc.id), {
        isExpired: true,
      });

      return {
        isValid: false,
        message: "This link has expired",
        status: "expired",
      };
    }

    return {
      isValid: true,
      message: "Link is valid",
      status: "valid",
      linkData: {
        invoiceData: linkData.invoiceData,
        email: linkData.email,
        expiresAt: expiresAt.toISOString(),
      },
    };
  } catch (error) {
    console.error("Error validating secure invoice link:", error);
    return {
      isValid: false,
      message: "Error validating link",
      status: "error",
    };
  }
};

// Marcar enlace de invoice como usado
export const markInvoiceLinkAsUsed = async (linkId) => {
  try {
    const q = query(
      collection(db, "secureInvoiceLinks"),
      where("linkId", "==", linkId),
      limit(1)
    );

    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const linkDoc = querySnapshot.docs[0];

      await updateDoc(doc(db, "secureInvoiceLinks", linkDoc.id), {
        isUsed: true,
        usedAt: Timestamp.fromDate(new Date()),
      });

      return true;
    }

    return false;
  } catch (error) {
    console.error("Error marking invoice link as used:", error);
    return false;
  }
};

// Obtener estadísticas de enlaces de invoice
export const getInvoiceLinkStats = async () => {
  try {
    const q = query(
      collection(db, "secureInvoiceLinks"),
      orderBy("createdAt", "desc")
    );

    const querySnapshot = await getDocs(q);

    const links = querySnapshot.docs.map((docItem) => ({
      id: docItem.id,
      ...docItem.data(),
      createdAt: docItem.data().createdAt.toDate(),
      expiresAt: docItem.data().expiresAt.toDate(),
    }));

    const now = new Date();

    const stats = {
      total: links.length,
      active: links.filter(
        (link) => !link.isUsed && !link.isExpired && now < link.expiresAt
      ).length,
      used: links.filter((link) => link.isUsed).length,
      expired: links.filter((link) => link.isExpired || now > link.expiresAt)
        .length,
    };

    return stats;
  } catch (error) {
    console.error("Error getting invoice link stats:", error);
    return { total: 0, active: 0, used: 0, expired: 0 };
  }
};

// Limpiar enlaces expirados de invoice
export const cleanupExpiredInvoiceLinks = async () => {
  try {
    const now = new Date();

    const q = query(
      collection(db, "secureInvoiceLinks"),
      where("expiresAt", "<", Timestamp.fromDate(now))
    );

    const querySnapshot = await getDocs(q);

    const deletePromises = querySnapshot.docs.map((docItem) =>
      deleteDoc(docItem.ref)
    );

    await Promise.all(deletePromises);

    return querySnapshot.size;
  } catch (error) {
    console.error("Error cleaning up expired invoice links:", error);
    return 0;
  }
};

// Obtener todos los enlaces de invoice para dashboard
export const getAllInvoiceLinks = async () => {
  try {
    const q = query(
      collection(db, "secureInvoiceLinks"),
      orderBy("createdAt", "desc")
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((docItem) => ({
      id: docItem.id,
      ...docItem.data(),
      createdAt: docItem.data().createdAt.toDate(),
      expiresAt: docItem.data().expiresAt.toDate(),
    }));
  } catch (error) {
    console.error("Error getting all invoice links:", error);
    return [];
  }
};